import { ColDef } from 'ag-grid-community'
import { Collapse } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { PageArea, PageProps } from '..'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { ScheduledOperationTimeDetail } from '../../../lib/functions/scheduledOperationTime'
import {
  storedUiStateWithToolbarToggleAdaptor,
  ToolbarToggleValue,
} from '../../components/toolbars/Toolbar/ToolbarToggle'
import BulkSheetComponent, {
  BulkSheet,
  BulkSheetSpecificProps,
  ROW_HEIGHT,
} from '../../containers/BulkSheet'
import ScheduledOperationTimeHeader from './Header'
import ScheduledOperationTimeOptions, {
  ColumnQuickFilterKey,
  ScheduledOperationTimeRow,
  ScheduledOperationTimeState,
} from './ScheduledOperationTimeOptions'
import ScheduledOperationTimeToolbar from './Toolbar'
import { usePageState } from '../../hooks/usePageState'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { pageComponent } from '../../higher-order-components/pageComponent'

type ScheduledOperationTimePageState = {
  toolbar: ToolbarToggleValue | undefined
}

const scheduledOperationTimeDefaultPageState = {
  toolbar: ToolbarToggleValue.DISPLAY,
}

const ScheduledOperationTime = (props: PageProps) => {
  const [bulkSheet, setBulkSheet] =
    useState<
      BulkSheet<
        BulkSheetSpecificProps,
        ScheduledOperationTimeDetail,
        ScheduledOperationTimeRow,
        ScheduledOperationTimeState
      >
    >()

  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortedColumns, setSortedColumns] = useState<string[]>([])
  const [rowHeight, setRowHeight] = useState<number>(ROW_HEIGHT.SMALL)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const scheduledOperationTimeOptions = useMemo(
    () => new ScheduledOperationTimeOptions(),
    []
  )

  const { toolbar, updatePageState } =
    usePageState<ScheduledOperationTimePageState>(
      props.uuid,
      scheduledOperationTimeDefaultPageState,
      storedUiStateWithToolbarToggleAdaptor
    )
  const onChangeToolbar = useCallback(
    (toolbar: ToolbarToggleValue | undefined) => {
      updatePageState({ toolbar })
    },
    []
  )

  const onChangeColumnFilter = useCallback(
    (value: ColumnQuickFilterKey) => {
      if (!bulkSheet) return
      if (value === ColumnQuickFilterKey.INITIAL) {
        bulkSheet.resetColumnAndFilterState()
      } else if (value === ColumnQuickFilterKey.RESTORE) {
        bulkSheet.openSavedBulkSheetUIStateDialog(
          UiStateKey.BulkSheetUIStateColumnAndFilter
        )
      }
    },
    [bulkSheet]
  )

  const onSearch = useCallback(
    searchCondition => {
      if (!bulkSheet) return
      scheduledOperationTimeOptions.onSearch(bulkSheet, searchCondition)
    },
    [bulkSheet]
  )

  return (
    <PageArea>
      <ScheduledOperationTimeHeader
        toolbar={toolbar}
        onChangeToolbar={onChangeToolbar}
        onClickExport={() => bulkSheet?.openExcelOutputColumnSelectDialog()}
        filteredColumns={filteredColumns}
        sortedColumns={sortedColumns}
        rowHeight={rowHeight}
        onClickChangeRowHeight={value => bulkSheet?.onChangeHeight(value)}
        onSearch={onSearch}
        year={bulkSheet?.state.year!}
        month={bulkSheet?.state.month!}
        isNotice={filteredColumns.length > 0 || sortedColumns.length > 0}
        onSubmit={() => bulkSheet?.onSubmit()}
        submitDisabled={submitDisabled}
        isLoading={isLoading}
        onCancel={() => bulkSheet?.onCancel()}
      />
      <Collapse in={!!toolbar} timeout={100}>
        <ScheduledOperationTimeToolbar
          toolbar={toolbar}
          onChangeColumnFilter={onChangeColumnFilter}
          filteredColumns={filteredColumns}
          sortedColumns={sortedColumns}
          onDeletedFilterColumn={column =>
            bulkSheet?.resetSpecificColumnFilter(column || '')
          }
        />
      </Collapse>
      <BulkSheetComponent
        {...props}
        options={scheduledOperationTimeOptions}
        hideHeader={true}
        hideToolbar={true}
        setBulkSheet={ctx => setBulkSheet(ctx)}
        setFilteredColumns={columns => setFilteredColumns(columns)}
        setSortedColumns={columns => setSortedColumns(columns)}
        setRowHeight={height => setRowHeight(height)}
        setSubmitDisabled={value => setSubmitDisabled(value)}
        setIsLoading={value => setIsLoading(value)}
      />
    </PageArea>
  )
}

export default pageComponent(projectPrivate(ScheduledOperationTime))
